/**
 * API client for the admin Users management endpoints.
 *
 *   GET    /admin/users              → AdminUserListResponse
 *   DELETE /admin/users/:userId      → 204
 *
 * Same `adminGuard` server-side gating as the rest of `/admin/*`.
 * Hand-written client because orval is gated on a swagger source the
 * API team hasn't published yet.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { apiClient } from '@/lib/axios';

// ── Types ───────────────────────────────────────────────────────────────────

export interface AdminUserRow {
  id: string;
  email: string;
  firstName: string | null;
  lastName: string | null;
  timezone: string;
  goal: string;
  fitnessLevel: string;
  trainingDays: number;
  timePerSessionMin: number | null;
  activeGymProfileId: number | null;
  hasActiveGymProfile: boolean;
  /** YYYY-MM-DD anchor for the curated Drive program. Null until the
   *  first sync. The Program page reads this to pre-fill the start
   *  date input when an athlete is selected. */
  programStartDate: string | null;
  /** ISO weekdays (1=Mon..7=Sun) the athlete trains on. Empty when
   *  not yet set — the sync falls back to consecutive day mapping. */
  programDaysOfWeek: number[];
  /** Library program this athlete's daily plans are built from. Null
   *  means they fall back to the library default. The per-week sync
   *  resolves content from THIS program, not from whatever the Program
   *  page's selector is showing. */
  activeProgramId: string | null;
  activeProgramName: string | null;
  createdAt: string;
}

export interface AdminUserListResponse {
  users: AdminUserRow[];
  total: number;
}

// ── List ────────────────────────────────────────────────────────────────────

const ADMIN_USERS_QUERY_KEY = ['admin', 'users'] as const;

async function getAdminUsers(): Promise<AdminUserListResponse> {
  const { data } = await apiClient.get<AdminUserListResponse>('/admin/users');
  return data;
}

export const useAdminUsers = () =>
  useQuery({
    queryKey: ADMIN_USERS_QUERY_KEY,
    queryFn: getAdminUsers,
  });

// ── Delete ──────────────────────────────────────────────────────────────────

async function deleteAdminUser(userId: string): Promise<void> {
  await apiClient.delete(`/admin/users/${userId}`);
}

export const useDeleteAdminUser = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteAdminUser,
    onSuccess: () => {
      // Invalidate the roster so the deleted row drops out of the table
      // on the next render. React-Query refetches in the background.
      void queryClient.invalidateQueries({ queryKey: ADMIN_USERS_QUERY_KEY });
    },
  });
};
